import { api } from "./index";

// Types
export interface PublicTender {
  id: string;
  description: string;
  category: 'GOODS' | 'SERVICES' | 'WORKS';
  stage: string;
  publishingDate: string;
  submissionDeadline: string;
  openingDate?: string;
  referenceNo: string;
  torSpecification?: string;
  requireRegistration?: boolean;
  requirePrebidMeeting?: boolean;
  prebidMeetingDate?: string;
  prebidMeetingVenue?: string;
  items?: Array<{
    id: string;
    itemName: string;
    description?: string;
    quantity: number;
    unit: string;
    order: number;
  }>;
  requirements?: Array<{
    id: string;
    documentType: string;
    description?: string;
    isRequired: boolean;
    order: number;
  }>;
  torDocument?: {
    id: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
    uploadedAt: string;
  };
}

export interface TorDocument {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  uploadedAt: string;
}

export interface PublicTenderFilters {
  search?: string;
  category?: string;
  page?: number;
  limit?: number;
}

export interface PublicTenderResponse {
  data: PublicTender[];
  meta: {
    total: number;
    totalPages: number;
    currentPage: number;
    limit: number;
  };
}

// Public Tender API (no authentication required)
export const publicTenderApi = {
  // Get published tenders
  getPublishedTenders: async (filters: PublicTenderFilters = {}) => {
    console.log('API call with filters:', filters);
    const response = await api.get<PublicTenderResponse>("/public/tenders", { params: filters });
    console.log('API response data:', response.data);
    return response.data;
  },

  // Get specific tender details
  getTenderById: async (id: string) => {
    const response = await api.get<PublicTender>(`/public/tenders/${id}`);
    return response.data;
  },

  // Get TOR document info
  getTorDocument: async (tenderId: string) => {
    const response = await api.get<TorDocument>(`/public/tenders/${tenderId}/tor-doc`);
    return response.data;
  },

  // Download TOR document
  downloadTorDocument: async (tenderId: string) => {
    // First, get the TOR document info to get the filename
    let filename = 'tor-document.txt';
    try {
      const torInfo = await publicTenderApi.getTorDocument(tenderId);
      if (torInfo?.fileName) {
        filename = torInfo.fileName;
      }
    } catch (error) {
      console.warn('Failed to get TOR document info, using default filename', error);
    }
    
    // Download the document
    const response = await api.get(`/public/tenders/${tenderId}/tor-doc/download`, {
      responseType: 'blob',
    });
    
    // Try to extract filename from response headers as fallback
    const contentDisposition = response.headers['content-disposition'] || 
                                response.headers['Content-Disposition'];
    
    if (contentDisposition && filename === 'tor-document.txt') {
      // Try to extract filename from Content-Disposition header
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match) {
        let extractedFilename = match[1];
        extractedFilename = extractedFilename.replace(/^["']|["']$/g, '');
        try {
          extractedFilename = decodeURIComponent(extractedFilename);
        } catch (e) {
          // If decoding fails, use as is
        }
        if (extractedFilename && extractedFilename !== 'undefined') {
          filename = extractedFilename;
        }
      }
    }
    
    // Get content type from headers
    const contentType = response.headers['content-type'] || 
                        response.headers['Content-Type'] || 
                        'application/octet-stream';
    
    // Create blob with correct mime type
    const blob = new Blob([response.data], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    
    // Create download link
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};
